import { useState } from "react";
import { Trash2, FileText, CheckCircle, AlertCircle, Loader2, Globe, Lock } from "lucide-react";
import { useRag } from "../RagContext";
import DocumentVisibilityModal from "./DocumentVisibilityModal";
import "./DocumentList.css";

const DOCUMENT_TYPES_LABELS = {
    bando_di_gara: "Bando di Gara",
    disciplinare_tecnico: "Disciplinare Tecnico",
    allegati_disciplinare: "Allegati al Disciplinare",
    schema_di_contratto: "Schema di Contratto",
    pronunciamenti_aters: "Pronunciamenti ATERSIR",
    offerta_tecnica_rti: "Offerta Tecnica RTI",
    allegati_offerta: "Allegati Offerta",
    generico: "Documento Generico",
};

function formatFileSize(bytes) {
    const n = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
    if (Number.isNaN(n) || n === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function StatusBadge({ status }) {
    if (status === "ready") {
        return (
            <span className="rag-doc__status rag-doc__status--ready">
                <CheckCircle size={12} />
                <span>Pronto</span>
            </span>
        );
    }
    if (status === "processing") {
        return (
            <span className="rag-doc__status rag-doc__status--processing">
                <Loader2 size={12} className="rag-doc__status-spinner" />
                <span>In elaborazione</span>
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span className="rag-doc__status rag-doc__status--failed">
                <AlertCircle size={12} />
                <span>Errore</span>
            </span>
        );
    }
    return <span className="rag-doc__status">{status}</span>;
}

function VisibilityIcon({ visibility, manageable, onClick }) {
    const isPublic = visibility !== "restricted";
    const Icon = isPublic ? Globe : Lock;
    const label = isPublic ? "Pubblico" : "Ristretto";
    const className = `rag-doc-list__visibility rag-doc-list__visibility--${isPublic ? "public" : "restricted"}${
        manageable ? " rag-doc-list__visibility--clickable" : ""
    }`;

    if (manageable) {
        return (
            <button
                type="button"
                className={className}
                onClick={onClick}
                aria-label={`Gestisci visibilità (${label})`}
                title={`${label} — clicca per gestire`}
            >
                <Icon size={14} />
                <span>{label}</span>
            </button>
        );
    }
    return (
        <span className={className} title={label}>
            <Icon size={14} />
            <span>{label}</span>
        </span>
    );
}

export default function DocumentList() {
    const { documents, loading, deleteDocument, canManageDocument } = useRag();
    const [visibilityModalDoc, setVisibilityModalDoc] = useState(null);

    const handleDelete = (e, id, filename) => {
        e.stopPropagation();
        if (window.confirm(`Eliminare il documento "${filename}"?`)) {
            deleteDocument(id);
        }
    };

    if (loading.documents && documents.length === 0) {
        return <p className="rag-doc-list__loading">Caricamento documenti…</p>;
    }

    if (documents.length === 0) {
        return (
            <div className="rag-doc-list__empty">
                <FileText size={32} />
                <p>Nessun documento caricato per questo bando.</p>
            </div>
        );
    }

    return (
        <div className="rag-doc-list">
            <table className="rag-doc-list__table">
                <thead>
                    <tr>
                        <th>Nome file</th>
                        <th>Tipo</th>
                        <th>Caricato da</th>
                        <th>Visibilità</th>
                        <th>Dimensione</th>
                        <th>Status</th>
                        <th>Chunks</th>
                        <th>Data</th>
                        <th aria-label="Azioni" />
                    </tr>
                </thead>
                <tbody>
                    {documents.map((doc) => {
                        const docTypeLabel =
                            doc.documentType && doc.documentType in DOCUMENT_TYPES_LABELS
                                ? DOCUMENT_TYPES_LABELS[doc.documentType]
                                : doc.documentType || "—";
                        const uploaderLabel = doc.uploadedByUsername || "—";
                        const manageable = canManageDocument(doc);
                        return (
                            <tr key={doc.id} className="rag-doc-list__row">
                                <td className="rag-doc-list__cell-filename">
                                    <FileText size={14} className="rag-doc-list__file-icon" />
                                    <span title={doc.filename}>{doc.filename}</span>
                                </td>
                                <td className="rag-doc-list__cell-type">{docTypeLabel}</td>
                                <td className="rag-doc-list__cell-uploader" title={uploaderLabel}>
                                    {uploaderLabel}
                                </td>
                                <td className="rag-doc-list__cell-visibility">
                                    <VisibilityIcon
                                        visibility={doc.visibility}
                                        manageable={manageable}
                                        onClick={() => setVisibilityModalDoc(doc)}
                                    />
                                </td>
                                <td className="rag-doc-list__cell-size">{formatFileSize(doc.fileSize)}</td>
                                <td><StatusBadge status={doc.status} /></td>
                                <td className="rag-doc-list__cell-chunks">{doc._count?.chunks ?? 0}</td>
                                <td className="rag-doc-list__cell-date">{formatDate(doc.createdAt)}</td>
                                <td className="rag-doc-list__cell-actions">
                                    {manageable && (
                                        <button
                                            type="button"
                                            className="rag-doc-list__delete"
                                            aria-label="Elimina documento"
                                            onClick={(e) => handleDelete(e, doc.id, doc.filename)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <DocumentVisibilityModal
                document={visibilityModalDoc}
                onClose={() => setVisibilityModalDoc(null)}
            />
        </div>
    );
}
